import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Phone, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Recapiti reali dell'agenzia
const PHONE_DISPLAY = "+39 334 960 4516";
const PHONE_HREF = "tel:+393349604516";
const WHATSAPP_HREF = "https://wa.me/393349604516";
const EMAIL_DISPLAY = "vianneascari04@gmail.com";
const EMAIL_HREF = "mailto:vianneascari04@gmail.com";

export default function ContactRail() {
  const [form, setForm] = useState({
    nominativo: "",
    telefono: "",
    richiesta: "Richiesta valutazione immobile per vendita",
    note: "",
  });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nominativo.trim() || !form.telefono.trim()) {
      toast.error("Inserisci nominativo e telefono per essere ricontattato.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Richiesta inviata. Ascari Home Consulting ti ricontatterà a breve.");
      setForm({ nominativo: "", telefono: "", richiesta: "Richiesta valutazione immobile per vendita", note: "" });
    } catch (err) {
      toast.error("Invio non riuscito. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[var(--paper)] border border-[var(--ink)]/20 rounded p-2.5 text-sm focus:border-[var(--gold)] outline-none transition-colors";

  return (
    <section id="contatto" data-testid="contact-section" className="bg-[var(--ink)] text-white py-24 md:py-32 px-6">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5 space-y-6">
          <span className="font-mono-lbl text-xs uppercase tracking-widest text-[var(--gold-light)] block">
            Canali Diretti
          </span>
          <h2 className="font-display font-bold text-[clamp(32px,3.8vw,52px)] leading-tight">
            Parla con Ascari Home Consulting.
          </h2>
          <p className="text-sm text-white/75 leading-relaxed max-w-md">
            Siamo a tua disposizione per perizie, sopralluoghi di cantiere e compravendite residenziali e commerciali.
          </p>

          <div className="space-y-4 pt-4 font-mono-lbl text-xs uppercase tracking-wider">
            <a
              href={WHATSAPP_HREF}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              className="flex items-center justify-between p-4 bg-white/5 border border-white/15 rounded hover:border-[var(--gold-light)] transition-colors"
            >
              <span className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[var(--gold-light)]" />
                WhatsApp Ascari
              </span>
              <span className="text-[var(--gold-light)]">Avvia chat →</span>
            </a>
            <a
              href={PHONE_HREF}
              data-testid="contact-phone-btn"
              className="flex items-center justify-between p-4 bg-white/5 border border-white/15 rounded hover:border-[var(--gold-light)] transition-colors"
            >
              <span className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[var(--gold-light)]" />
                Telefono Diretto
              </span>
              <span className="text-[var(--gold-light)]">Chiama ora →</span>
            </a>
            <a
              href={EMAIL_HREF}
              data-testid="contact-email-btn"
              className="flex items-center justify-between p-4 bg-white/5 border border-white/15 rounded hover:border-[var(--gold-light)] transition-colors"
            >
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[var(--gold-light)]" />
                Email
              </span>
              <span className="text-[var(--gold-light)] normal-case tracking-normal">{EMAIL_DISPLAY}</span>
            </a>
            <p className="text-[10px] text-white/40 normal-case tracking-normal">
              Telefono e WhatsApp: {PHONE_DISPLAY}
            </p>
            <div className="pt-4 border-t border-white/10 text-[11px] text-white/60 normal-case tracking-normal space-y-1">
              <p className="font-semibold text-white/80">Via Giardini 1053, 41122 Modena</p>
              <p>Lun–Ven 09:00–20:00 · Sab 09:00–12:00 · Dom chiuso</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 bg-white text-[var(--ink)] p-8 md:p-10 rounded-xl">
          <h3 className="font-display font-bold text-2xl mb-1">Invia Messaggio</h3>
          <p className="font-mono-lbl text-xs text-[var(--ink)]/60 mb-6 uppercase">
            Modulo di richiesta valutazione e contatto
          </p>
          <form className="space-y-4" onSubmit={submit} data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="cf-nome" className="block font-mono-lbl text-[11px] uppercase tracking-wider mb-1">
                  Nominativo
                </label>
                <input
                  id="cf-nome"
                  data-testid="contact-form-name"
                  type="text"
                  placeholder="Nome completo"
                  value={form.nominativo}
                  onChange={update("nominativo")}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-tel" className="block font-mono-lbl text-[11px] uppercase tracking-wider mb-1">
                  Telefono
                </label>
                <input
                  id="cf-tel"
                  data-testid="contact-form-phone"
                  type="tel"
                  placeholder="Cellulare"
                  value={form.telefono}
                  onChange={update("telefono")}
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label htmlFor="cf-richiesta" className="block font-mono-lbl text-[11px] uppercase tracking-wider mb-1">
                Richiesta
              </label>
              <select
                id="cf-richiesta"
                data-testid="contact-form-request"
                value={form.richiesta}
                onChange={update("richiesta")}
                className={inputCls}
              >
                <option>Richiesta valutazione immobile per vendita</option>
                <option>Informazioni su immobile in vendita</option>
                <option>Informazioni su immobile in affitto</option>
                <option>Pratica tecnica geometra (catastale, urbanistica, APE/ARE)</option>
                <option>Richiesta consulenza mutuo</option>
              </select>
            </div>
            <div>
              <label htmlFor="cf-note" className="block font-mono-lbl text-[11px] uppercase tracking-wider mb-1">
                Note immobile
              </label>
              <textarea
                id="cf-note"
                data-testid="contact-form-notes"
                rows="3"
                placeholder="Informazioni sulla casa o sul locale commerciale..."
                value={form.note}
                onChange={update("note")}
                className={inputCls}
              ></textarea>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit"
              className="w-full py-3.5 bg-[var(--ink)] text-[var(--gold-light)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors disabled:opacity-60"
            >
              {sending ? "Invio in corso..." : "Invia Richiesta"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}
